import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../api/axios'
import posthog from 'posthog-js'
import { CheckCircle } from 'lucide-react'

type Message = {
  id: string
  type: 'ink' | 'user'
  text: string
  typing?: boolean
}

type Step = 'landing' | 'name' | 'email' | 'company' | 'submitting' | 'success'

const FEATURES = [
  'Full Portal Access',
  'Submit Jobs via Email or Portal',
  'Personal Artwork Dashboard',
  'Secure File Storage',
  'Priority Support'
]

export default function TrialPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>('landing')
  const stepRef = useRef<Step>('landing')
  const autoStart = new URLSearchParams(window.location.search).get('start') === 'true'
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [userName, setUserName] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [userCompany, setUserCompany] = useState('')
  const emailRef = useRef('')
  const [error, setError] = useState('')
  const [successEmail, setSuccessEmail] = useState('')
  const [trialEnabled, setTrialEnabled] = useState(true)
  const [trialPlan, setTrialPlan] = useState<any>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    posthog.opt_in_capturing()
    posthog.capture('$pageview')
    return () => { posthog.opt_out_capturing() }
  }, [])

  // Clarity
  useEffect(() => {
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.innerHTML = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", "w0tb8a9fyj");`
    document.head.appendChild(script)
    return () => { try { document.head.removeChild(script) } catch {} }
  }, [])

  useEffect(() => {
    const urlPlanId = new URLSearchParams(window.location.search).get('plan')
    ;(window as any).__trialPlanId = urlPlanId
    api.get(`/payment/trial-settings${urlPlanId ? `?planId=${urlPlanId}` : ''}`).then(res => {
      setTrialEnabled(res.data.enabled)
      setTrialPlan(res.data.trialPlan)
      if (autoStart && res.data.enabled) startConversation()
    }).catch(() => {})
  }, [])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const addInkMessage = (text: string, delay = 600) => {
    const id = Date.now().toString()
    // Show typing indicator first
    setMessages(prev => [...prev, { id, type: 'ink', text: '', typing: true }])
    setTimeout(() => {
      setMessages(prev => prev.map(m => m.id === id ? { ...m, text, typing: false } : m))
      setTimeout(() => inputRef.current?.focus(), 100)
    }, delay)
  }

  const addUserMessage = (text: string) => {
    setMessages(prev => [...prev, { id: Date.now().toString(), type: 'user', text }])
  }

  const startConversation = () => {
    if (!trialEnabled) return
    stepRef.current = 'name'
    setStep('name')
    setMessages([])
    setTimeout(() => {
      addInkMessage("Hi there! Welcome to Inkcredible Studios. What should we call you?", 400)
    }, 100)
  }

  const handleSend = async () => {
    const value = input.trim()
    if (!value) return
    setError('')
    setInput('')

    if (stepRef.current === 'name') {
      addUserMessage(value)
      setUserName(value)
      stepRef.current = 'email'
      setStep('email')
      setTimeout(() => addInkMessage(`Nice to meet you, ${value.split(' ')[0]}! What's the best email address to reach you?`), 100)
    } else if (stepRef.current === 'email') {
      if (!value.includes('@') || !value.includes('.')) {
        setError('Please enter a valid email address')
        return
      }
      addUserMessage(value)
      setUserEmail(value)
      emailRef.current = value
      stepRef.current = 'company'
      setStep('company')
      setTimeout(() => addInkMessage(`Perfect! And what company are you representing?`), 100)
    } else if (stepRef.current === 'company') {
      addUserMessage(value)
      setUserCompany(value)
      stepRef.current = 'submitting'
      setStep('submitting')
      addInkMessage(`Awesome! Setting up your free trial now... ✨`, 300)

      try {
        const nameParts = userName.trim().split(' ')
        const firstName = nameParts[0]
        const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : ''
        const res = await api.post('/payment/trial-signup', {
          firstName, lastName, email: emailRef.current,
          companyName: value,
          ...((window as any).__trialPlanId ? { planId: (window as any).__trialPlanId } : {})
        })
        const email = res.data.email || emailRef.current
      setSuccessEmail(email)
        setTimeout(() => {
          addInkMessage(`You're all set, ${userName.split(' ')[0]}! Your trial account has been created. Check your email for login details.`, 800)
          setStep('success')
        }, 1500)
      } catch (e: any) {
        const errMsg = e.response?.data?.error || 'Something went wrong. Please try again.'
        setError(errMsg)
        addInkMessage(`Hmm, something went wrong. ${errMsg} Want to try again?`, 400)
        stepRef.current = 'company'
        setStep('company')
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSend()
  }

  const isChat = step !== 'landing'
  const isDone = stepRef.current === 'success' || stepRef.current === 'submitting'

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fff9f5 0%, #fff 50%, #f0f7ff 100%)', fontFamily: "'Inter', sans-serif", display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '20px 32px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid #f0f0f0', background: '#fff' }}>
        <a href="https://subscribe.inkcrediblestudios.com/" target="_blank" rel="noreferrer">
          <img src="/Logo_White_Bg_Horizontal.png" alt="Inkcredible Studios" style={{ height: 40, objectFit: 'contain' }} />
        </a>
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 16px' }}>
        {!isChat ? (
          /* Landing state */
          <div style={{ maxWidth: 600, width: '100%', textAlign: 'center' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff3eb', border: '1px solid #fed7aa', borderRadius: 100, padding: '6px 16px', marginBottom: 24 }}>
              <span style={{ color: '#f97316', fontSize: 12, fontWeight: 600 }}>
                ✦ {trialPlan?.periodDays ?? 30}-Days Free Trial · No credit card required
              </span>
            </div>
            <h1 style={{ color: '#071829', fontSize: 40, fontWeight: 800, lineHeight: 1.2, marginBottom: 16 }}>
              Try Inkcredible<br />
              <span style={{ color: '#f97316' }}>Free for 30 Days</span>
            </h1>
            <p style={{ color: '#6b7280', fontSize: 16, lineHeight: 1.6, marginBottom: 20 }}>
              Get <strong style={{ color: '#071829' }}>{trialPlan?.jobCount ?? 30} jobs</strong> over <strong style={{ color: '#071829' }}>{trialPlan?.periodDays ?? 30} days</strong> — completely free. Experience our dedicated artwork team and client portal with zero commitment.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginBottom: 12 }}>
              {[
                `${trialPlan?.jobCount ?? 30} Jobs included`,
                `${trialPlan?.periodDays ?? 30} Days access`,
                'Vector Service quality',
                'Client portal access',
                'Dedicated artwork team',
              ].map(f => (
                <span key={f} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 100, padding: '6px 14px', fontSize: 13, color: '#374151' }}>{f}</span>
              ))}
            </div>
            <p style={{ color: '#9ca3af', fontSize: 13, marginBottom: 32 }}>
              ⚠️ Complex jobs not included in trial · Standard artwork only
            </p>
            <div style={{ display: 'flex', gap: 20, justifyContent: 'center', marginBottom: 32, flexWrap: 'wrap' }}>
              {['No commitment', 'No credit card required', 'Cancel anytime'].map(t => (
                <span key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#6b7280', fontSize: 13 }}>
                  <span style={{ color: '#22c55e', fontWeight: 700 }}>✓</span> {t}
                </span>
              ))}
            </div>
            <button
              onClick={startConversation}
              disabled={!trialEnabled}
              style={{ background: trialEnabled ? '#f97316' : '#d1d5db', color: '#fff', fontWeight: 700, fontSize: 16, padding: '16px 48px', borderRadius: 14, border: 'none', cursor: trialEnabled ? 'pointer' : 'not-allowed', boxShadow: trialEnabled ? '0 8px 24px rgba(249,115,22,0.3)' : 'none', transition: 'all 0.2s' }}>
              {trialEnabled ? 'Start Free Trial' : 'Trial Not Available'}
            </button>
            {!trialEnabled && <p style={{ color: '#ef4444', fontSize: 13, marginTop: 12 }}>Free trial signups are currently not available.</p>}
          </div>
        ) : (
          /* Chat conversation */
          <div style={{ maxWidth: 520, width: '100%' }}>
            {/* Chat header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <div style={{ width: 44, height: 44, background: '#f97316', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 18, flexShrink: 0 }}>I</div>
              <div>
                <p style={{ color: '#071829', fontWeight: 700, fontSize: 15, margin: 0 }}>Ink</p>
                <p style={{ color: '#9ca3af', fontSize: 12, margin: 0 }}>Inkcredible Studios Assistant</p>
              </div>
            </div>

            {/* Messages */}
            <div style={{ minHeight: 200, marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {messages.map(m => (
                <div key={m.id} style={{ display: 'flex', justifyContent: m.type === 'user' ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    maxWidth: '80%',
                    padding: '12px 16px',
                    borderRadius: m.type === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    background: m.type === 'user' ? '#f97316' : '#fff',
                    color: m.type === 'user' ? '#fff' : '#071829',
                    fontSize: 14,
                    lineHeight: 1.5,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    border: m.type === 'ink' ? '1px solid #f3f4f6' : 'none'
                  }}>
                    {m.typing ? (
                      <span style={{ display: 'flex', gap: 4, alignItems: 'center', padding: '2px 0' }}>
                        {[0, 1, 2].map(i => (
                          <span key={i} style={{
                            width: 8, height: 8, borderRadius: '50%', background: '#d1d5db',
                            animation: 'bounce 1.2s ease-in-out infinite',
                            animationDelay: `${i * 0.2}s`
                          }} />
                        ))}
                      </span>
                    ) : m.text}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Error */}
            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', marginBottom: 12, color: '#ef4444', fontSize: 13 }}>
                {error}
              </div>
            )}

            {/* Input */}
            {!isDone && (
              <div style={{ display: 'flex', gap: 10, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '8px 8px 8px 16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <input
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    step === 'name' ? 'Your name...' :
                    step === 'email' ? 'Your email address...' :
                    step === 'company' ? 'Your company name...' : ''
                  }
                  style={{ flex: 1, border: 'none', outline: 'none', fontSize: 14, color: '#071829', background: 'transparent' }}
                  autoFocus
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim()}
                  style={{ background: input.trim() ? '#f97316' : '#f3f4f6', color: input.trim() ? '#fff' : '#9ca3af', border: 'none', borderRadius: 10, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: input.trim() ? 'pointer' : 'not-allowed', flexShrink: 0, fontSize: 18, transition: 'all 0.2s' }}>
                  ↑
                </button>
              </div>
            )}

            {step === 'success' && (
              <div style={{ textAlign: 'center', marginTop: 24 }}>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
                  <p style={{ color: '#166534', fontSize: 13, margin: 0 }}>
                    <CheckCircle className="w-4 h-4 text-green-500 inline mr-1" /> Login details sent to <strong>{emailRef.current}</strong>
                  </p>
                </div>
                <button
                  onClick={() => navigate('/login')}
                  style={{ background: '#071829', color: '#fff', fontWeight: 600, fontSize: 14, padding: '12px 32px', borderRadius: 10, border: 'none', cursor: 'pointer' }}>
                  Go to Login →
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bounce animation */}
      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  )
}